import { useQuery } from '@tanstack/react-query';
import { getWeeklyDigest, type WeeklyDigestResponse } from '../../reports/api/weeklyDigest';
import { useAuth } from '../../../contexts/AuthContext';
import { useStudentMe } from '../../roster/hooks/useStudentMe';
import { useTutorQueryIdentity } from '../../tutor/hooks/useTutorQueryIdentity';
import { weeklyStudentDigestQueryKey } from '../queryKeys';
import { isDashboardIdentityReady, resolveDashboardStudentGrade } from '../utils/dashboardQueryGating';

export const WEEKLY_STUDENT_DIGEST_QUERY_KEY = ['student-dashboard', 'weekly-digest'] as const;
const STALE_TIME_MS = 5 * 60 * 1000;

function weekRangeLast7(): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
}

export function useWeeklyStudentDigest(options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const { grade, isRosterLoading } = useStudentMe();
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const studentGrade = resolveDashboardStudentGrade(grade);
  const identityReady = isDashboardIdentityReady(membershipKey, studentKey);
  const studentId = user?.id ?? '';

  const enabled =
    (options?.enabled ?? true) &&
    identityReady &&
    !isRosterLoading &&
    Boolean(studentId) &&
    studentGrade !== undefined;

  const queryKey =
    identityReady && studentGrade !== undefined
      ? weeklyStudentDigestQueryKey({ membershipKey, studentKey, grade: studentGrade })
      : [...WEEKLY_STUDENT_DIGEST_QUERY_KEY, 'disabled'];

  const query = useQuery<WeeklyDigestResponse, Error>({
    queryKey,
    queryFn: () => {
      const { start, end } = weekRangeLast7();
      return getWeeklyDigest({
        studentId,
        startDate: start,
        endDate: end,
        grade: studentGrade,
      });
    },
    enabled,
    staleTime: STALE_TIME_MS,
  });

  return {
    digest: query.data ?? null,
    loading: enabled && query.isLoading,
    error: query.error,
    reload: () => void query.refetch(),
  };
}
